import { defineMessages } from 'react-intl';

import {
	useObjectSidebarActions,
	useIsObjectSidebarShown,
	usePanel,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { PanelName } from '@confluence/object-sidebar-api/entry-points/PanelName.ts';
import { LoadableLazy } from '@confluence/loadable';
import type { PanelProps } from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';

const DetailsPanelHeaderIcon = LoadableLazy<PanelProps>({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-useShowDetailsPanel" */ './DetailsPanelHeaderIcon.tsx'
			)
		).DetailsPanelHeaderIcon,
});

const DetailsPanelBody = LoadableLazy<PanelProps>({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-useShowDetailsPanel" */ './DetailsPanelBody.tsx'))
			.DetailsPanelBody,
});

export const i18n = defineMessages({
	detailsPanelHeaderLabel: {
		id: 'details-panel.panel.header.label',
		defaultMessage: 'Details',
		description: 'Label for the details panel header',
	},
});

export const useShowDetailsPanel = () => {
	const { showObjectSidebar } = useObjectSidebarActions();
	const isObjectSidebarShown = useIsObjectSidebarShown();
	const panel = usePanel();

	const showDetailsPanel = ({ highlightLabels }: { highlightLabels?: boolean } = {}) => {
		showObjectSidebar(
			{
				id: PanelName.DetailsPanel,
				headerComponentElements: {
					HeaderIcon: DetailsPanelHeaderIcon,
					headerLabel: i18n.detailsPanelHeaderLabel,
				},
				BodyComponent: DetailsPanelBody,
				props: {
					highlightLabels,
				},
			},
			'push',
		);
	};

	return {
		isDetailsPanelShown: isObjectSidebarShown && panel?.id === PanelName.DetailsPanel,
		showDetailsPanel,
	};
};
